import React, { useEffect, useRef } from 'react';
import { Undo2, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import useInlineRevert from './useInlineRevert';

// "Undo this publish" with an in-place confirmation (never window.confirm),
// then Undoing… → Reverted / Couldn't undo. docs/inline-edit-lab.md §S2.3.
//
//   requestId      the inline edit request to undo
//   onReverted(er) called once with the reverted request
//   onStateChange(state, message)  lets a host show the status itself
//   hideStatus     don't render the status line (the host shows it)
//   initialState   'undoing' when the undo is already running (after a reload)
//   label / buttonClassName  the ask button's words and look

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60';

export function undoStatusText(state, message) {
  if (state === 'undoing') return 'Undoing… This usually takes a few minutes.';
  // The rebuild after the undo isn't tracked here, hence "within a few minutes".
  if (state === 'reverted') return 'Undone. Your website will show the previous version within a few minutes.';
  if (state === 'failed') return `Couldn’t undo. ${message || 'Your changes are still live. Try again, or send an edit request.'}`;
  return '';
}

const pillClasses = 'self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.12] text-[12px] text-white hover:border-white/[0.25]';

export default function InlineUndoButton({
  requestId, onReverted, onStateChange, hideStatus = false, initialState, label = 'Undo this publish', buttonClassName = pillClasses, className = '',
}) {
  const { state, message, retryable, ask, cancel, confirm } = useInlineRevert(requestId, { onReverted, initialState });
  const askRef = useRef(null);
  const keepRef = useRef(null);
  const prevState = useRef(state);

  useEffect(() => { onStateChange?.(state, message); }, [state, message, onStateChange]);

  // Focus follows the question: into it on ask, back to the button on cancel.
  useEffect(() => {
    if (state === 'confirming') keepRef.current?.focus();
    else if (state === 'idle' && prevState.current === 'confirming') askRef.current?.focus();
    prevState.current = state;
  }, [state]);

  const status = undoStatusText(state, message);
  const questionId = `inline-undo-q-${requestId}`;

  return (
    <div className={`flex flex-col gap-2 ${className}`} data-testid="inline-undo">
      {(state === 'idle' || (state === 'failed' && retryable)) && (
        <button type="button" ref={askRef} onClick={ask} className={`${focusRing} ${buttonClassName}`}>
          <Undo2 className="w-3 h-3" aria-hidden="true" /> {state === 'failed' ? 'Try undoing again' : label}
        </button>
      )}
      {state === 'confirming' && (
        <div role="group" aria-labelledby={questionId} className="rounded-lg border border-yellow-400/25 bg-yellow-400/[0.06] p-3 flex flex-col gap-2">
          <p id={questionId} className="text-[12px] text-yellow-100/90">
            Undo this publish? Your website goes back to how it was before these changes.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={confirm}
              className={`${focusRing} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 text-[12px] text-red-200 hover:bg-red-500/25`}>
              <Undo2 className="w-3 h-3" aria-hidden="true" /> Undo publish
            </button>
            <button type="button" ref={keepRef} onClick={cancel}
              className={`${focusRing} px-3 py-1.5 rounded-full border border-white/[0.12] text-[12px] text-gray-300 hover:text-white`}>
              Keep it
            </button>
          </div>
        </div>
      )}
      {!hideStatus && (
        <p role="status" aria-live="polite" className={status ? 'text-[12px] leading-relaxed' : 'sr-only'}>
          {state === 'undoing' && <Loader2 className="inline w-3.5 h-3.5 mr-1.5 animate-spin text-gray-300" aria-hidden="true" />}
          {state === 'reverted' && <CheckCircle2 className="inline w-3.5 h-3.5 mr-1.5 text-green-300" aria-hidden="true" />}
          {state === 'failed' && <AlertTriangle className="inline w-3.5 h-3.5 mr-1.5 text-red-300" aria-hidden="true" />}
          <span className={state === 'failed' ? 'text-red-300' : state === 'reverted' ? 'text-green-300' : 'text-gray-300'}>{status}</span>
        </p>
      )}
    </div>
  );
}
